import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  trackApplicationAPI,
  trackApplicationByLinkAPI,
  fetchStatusLogsAPI,
  fetchForwardLogsAPI,
  fetchPublicApplicationSmsHistoryAPI,
  fetchSharedDocumentsAPI,
} from '../../services/vatandasApi';

// ---------------------------------------------------------------------
// Başvuru takibi thunk (manuel giriş: telefon + takip no)
// ---------------------------------------------------------------------
export const trackApplication = createAsyncThunk(
  'vatandasTracking/trackApplication',
  async ({ phoneNumber, trackingNumber }, { rejectWithValue }) => {
    try {
      const response = await trackApplicationAPI(phoneNumber, trackingNumber);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message || 'Başvuru sorgulanamadı.');
    }
  }
);

// ---------------------------------------------------------------------
// Başvuru takibi thunk (direkt link: ?t=TOKEN)
// ---------------------------------------------------------------------
export const trackApplicationByLink = createAsyncThunk(
  'vatandasTracking/trackApplicationByLink',
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await trackApplicationByLinkAPI(token);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message || 'Takip bağlantısı ile başvuru açılamadı.');
    }
  }
);

// ---------------------------------------------------------------------
// Statü loglarını getiren thunk (bilgi notu için)
// Hem manuel akış hem link akışı desteklenir
// ---------------------------------------------------------------------
export const fetchStatusLogs = createAsyncThunk(
  'vatandasTracking/fetchStatusLogs',
  async ({ trackingNumber, phoneNumber, applicationId }, { rejectWithValue }) => {
    try {
      const response = await fetchStatusLogsAPI({
        trackingNumber,
        phoneNumber,
        applicationId,
      });

      const data = response?.data;

      if (Array.isArray(data)) {
        if (!applicationId) {
          return data;
        }

        return data.filter(
          (log) => String(log.application) === String(applicationId)
        );
      }

      if (data && Array.isArray(data.results)) {
        if (!applicationId) {
          return data.results;
        }

        return data.results.filter(
          (log) => String(log.application) === String(applicationId)
        );
      }

      return [];
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.detail || error.response.data);
      }
      return rejectWithValue(error.message || 'Statü logları alınamadı.');
    }
  }
);

export const fetchForwardLogs = createAsyncThunk(
  'vatandasTracking/fetchForwardLogs',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const response = await fetchForwardLogsAPI(applicationId);
      const data = response?.data;

      if (Array.isArray(data)) {
        return data;
      }

      if (data && Array.isArray(data.results)) {
        return data.results;
      }

      return [];
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.detail || error.response.data);
      }
      return rejectWithValue(error.message || 'Yönlendirme geçmişi alınamadı.');
    }
  }
);

export const fetchSmsHistory = createAsyncThunk(
  'vatandasTracking/fetchSmsHistory',
  async ({ token, trackingNumber, phoneNumber }, { rejectWithValue }) => {
    try {
      const response = await fetchPublicApplicationSmsHistoryAPI({
        token,
        trackingNumber,
        phoneNumber,
      });

      const data = response?.data;

      if (Array.isArray(data)) {
        return data;
      }

      if (data && Array.isArray(data.results)) {
        return data.results;
      }

      return [];
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.detail || error.response.data);
      }
      return rejectWithValue(error.message || 'SMS geçmişi alınamadı.');
    }
  }
);

// ---------------------------------------------------------------------
// Vatandaşa teslim edilen belgeler (public) — yalnız başvuru kapandıktan sonra döner
// ---------------------------------------------------------------------
export const fetchSharedDocuments = createAsyncThunk(
  'vatandasTracking/fetchSharedDocuments',
  async ({ token, trackingNumber, phoneNumber }, { rejectWithValue }) => {
    try {
      const response = await fetchSharedDocumentsAPI({ token, trackingNumber, phoneNumber });
      const data = response?.data;
      if (data && Array.isArray(data.items)) return data.items;
      if (Array.isArray(data)) return data;
      return [];
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.detail || error.response.data);
      }
      return rejectWithValue(error.message || 'Belgeler alınamadı.');
    }
  }
);

const initialState = {
  applicationData: null,

  // ana yükleme durumu (başvurunun açılması)
  status: 'idle',
  error: null,

  // tracking detay parçaları
  statusLogs: [],
  forwardLogs: [],
  smsHistory: [],
  sharedDocuments: [],

  // ayrı loading alanları
  statusLogsStatus: 'idle',
  forwardLogsStatus: 'idle',
  smsHistoryStatus: 'idle',
  sharedDocumentsStatus: 'idle',

  // ayrı hata alanları
  statusLogsError: null,
  forwardLogsError: null,
  smsHistoryError: null,
  sharedDocumentsError: null,

  // 'manual' | 'link' | null
  accessMode: null,
};

const vatandasTrackingSlice = createSlice({
  name: 'vatandasTracking',
  initialState,
  reducers: {
    // -----------------------------------------------------------------
    // Form reset / yeni sorgu / geçersiz token sonrası temiz başlangıç
    // -----------------------------------------------------------------
    resetTrackingState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // ===============================================================
      // Manuel takip
      // ===============================================================
     .addCase(trackApplication.pending, (state) => {
  state.status = 'loading';
  state.error = null;
  state.applicationData = null;

  state.statusLogs = [];
  state.forwardLogs = [];
  state.smsHistory = [];

  state.statusLogsStatus = 'idle';
  state.forwardLogsStatus = 'idle';
  state.smsHistoryStatus = 'idle';

  state.statusLogsError = null;
  state.forwardLogsError = null;
  state.smsHistoryError = null;

  state.accessMode = 'manual';
})
     .addCase(trackApplication.fulfilled, (state, action) => {
  state.status = 'succeeded';
  state.applicationData = action.payload;
  state.error = null;
  state.accessMode = 'manual';
})
   .addCase(trackApplication.rejected, (state, action) => {
  state.status = 'failed';
  state.error = action.payload;
  state.applicationData = null;

  state.statusLogs = [];
  state.forwardLogs = [];
  state.smsHistory = [];

  state.statusLogsStatus = 'idle';
  state.forwardLogsStatus = 'idle';
  state.smsHistoryStatus = 'idle';

  state.statusLogsError = null;
  state.forwardLogsError = null;
  state.smsHistoryError = null;

  state.accessMode = 'manual';
})

      // ===============================================================
      // Link ile direkt takip
      // ===============================================================
      .addCase(trackApplicationByLink.pending, (state) => {
  state.status = 'loading';
  state.error = null;
  state.applicationData = null;

  state.statusLogs = [];
  state.forwardLogs = [];
  state.smsHistory = [];

  state.statusLogsStatus = 'idle';
  state.forwardLogsStatus = 'idle';
  state.smsHistoryStatus = 'idle';

  state.statusLogsError = null;
  state.forwardLogsError = null;
  state.smsHistoryError = null;

  state.accessMode = 'link';
})
      .addCase(trackApplicationByLink.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.applicationData = action.payload;
        state.error = null;
        state.accessMode = 'link';
      })
    .addCase(trackApplicationByLink.rejected, (state, action) => {
  state.status = 'failed';
  state.error = action.payload;
  state.applicationData = null;

  state.statusLogs = [];
  state.forwardLogs = [];
  state.smsHistory = [];

  state.statusLogsStatus = 'idle';
  state.forwardLogsStatus = 'idle';
  state.smsHistoryStatus = 'idle';

  state.statusLogsError = null;
  state.forwardLogsError = null;
  state.smsHistoryError = null;

  state.accessMode = 'link';
})

      // ===============================================================
      // Statü logları / bilgi notu
      // ===============================================================
      .addCase(fetchStatusLogs.pending, (state) => {
  state.statusLogsStatus = 'loading';
  state.statusLogsError = null;
  state.statusLogs = [];
})
.addCase(fetchStatusLogs.fulfilled, (state, action) => {
  state.statusLogsStatus = 'succeeded';
  state.statusLogs = Array.isArray(action.payload) ? action.payload : [];
  state.statusLogsError = null;
})
.addCase(fetchStatusLogs.rejected, (state, action) => {
  state.statusLogsStatus = 'failed';
  state.statusLogs = [];
  state.statusLogsError = action.payload || 'Statü logları alınamadı.';
})

.addCase(fetchForwardLogs.pending, (state) => {
  state.forwardLogsStatus = 'loading';
  state.forwardLogsError = null;
  state.forwardLogs = [];
})
.addCase(fetchForwardLogs.fulfilled, (state, action) => {
  state.forwardLogsStatus = 'succeeded';
  state.forwardLogs = Array.isArray(action.payload) ? action.payload : [];
  state.forwardLogsError = null;
})
.addCase(fetchForwardLogs.rejected, (state, action) => {
  state.forwardLogsStatus = 'failed';
  state.forwardLogs = [];
  state.forwardLogsError = action.payload || 'Yönlendirme geçmişi alınamadı.';
})

.addCase(fetchSmsHistory.pending, (state) => {
  state.smsHistoryStatus = 'loading';
  state.smsHistoryError = null;
  state.smsHistory = [];
})
.addCase(fetchSmsHistory.fulfilled, (state, action) => {
  state.smsHistoryStatus = 'succeeded';
  state.smsHistory = Array.isArray(action.payload) ? action.payload : [];
  state.smsHistoryError = null;
})
.addCase(fetchSmsHistory.rejected, (state, action) => {
  state.smsHistoryStatus = 'failed';
  state.smsHistory = [];
  state.smsHistoryError = action.payload || 'SMS geçmişi alınamadı.';
})

.addCase(fetchSharedDocuments.pending, (state) => {
  state.sharedDocumentsStatus = 'loading';
  state.sharedDocumentsError = null;
  state.sharedDocuments = [];
})
.addCase(fetchSharedDocuments.fulfilled, (state, action) => {
  state.sharedDocumentsStatus = 'succeeded';
  state.sharedDocuments = Array.isArray(action.payload) ? action.payload : [];
  state.sharedDocumentsError = null;
})
.addCase(fetchSharedDocuments.rejected, (state, action) => {
  state.sharedDocumentsStatus = 'failed';
  state.sharedDocuments = [];
  state.sharedDocumentsError = action.payload || 'Belgeler alınamadı.';
});
  },
});

export const { resetTrackingState } = vatandasTrackingSlice.actions;

export default vatandasTrackingSlice.reducer;